import { useQuery } from "@tanstack/react-query";
import { Plus, Trash2, Undo2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import { Btn, Field, Modal } from "./wf";
import { api } from "../lib/api";

const OTHER = "__other__";
const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/** Ask why a completed project item is being sent back to the crew. */
export function RejectTaskDialog({ task, onClose, onSend }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (task) setReason("");
  }, [task]);
  const send = async () => {
    setBusy(true);
    const ok = await onSend(reason.trim());
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <Modal open={!!task} onClose={onClose} title="Send Back to Crew" testid="reject-task-sheet">
      <p className="text-sm text-wf-mute">{task?.title} · it reopens so the crew can fix it and mark it complete again</p>
      <Field multiline testid="reject-task-reason" value={reason} onChange={setReason} placeholder="What needs to be fixed?" maxLength={1000} />
      <div className="flex justify-end gap-2">
        <Btn variant="ghost" onClick={onClose} data-testid="reject-task-cancel">Cancel</Btn>
        <Btn variant="danger" icon={Undo2} loading={busy} disabled={!reason.trim()} onClick={send} data-testid="reject-task-send">Send Back</Btn>
      </div>
    </Modal>
  );
}

/** Edit the parts used on one project item. Saved parts are added into the job's Parts Used. */
export function TaskPartsDialog({ task, onClose, onSave, heading = "Parts Used", note = "added to the job's Parts Used" }) {
  const catalog = useQuery({ queryKey: ["parts"], queryFn: () => api("/parts"), enabled: !!task });
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (task) setRows((task.parts ?? []).map((p) => ({ id: p.id, part_id: p.part_id, name: p.name, part_number: p.part_number, qty: String(p.quantity), custom: !p.part_id })));
  }, [task]);
  const patch = (id, v) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...v } : r)));
  const save = async () => {
    const clean = rows.filter((r) => r.name.trim() || r.qty.trim());
    if (clean.find((r) => !r.name.trim() || !(parseFloat(r.qty) > 0))) return toast.error("Each part needs a name and a quantity above 0");
    setSaving(true);
    const ok = await onSave(clean.map((r) => ({ id: r.id, part_id: r.part_id, name: r.name.trim(), part_number: r.part_number, quantity: parseFloat(r.qty) })));
    setSaving(false);
    if (ok) onClose();
  };
  const sel = "h-11 w-full rounded-xl border border-wf-line bg-wf-s2 px-3 text-sm outline-none focus:border-wf-brand";
  return (
    <Modal open={!!task} onClose={onClose} title={heading} testid="task-parts-sheet" wide>
      <p className="text-sm text-wf-mute">{task?.title} · {note}</p>
      <div className="flex flex-col gap-2">
        {rows.map((r, i) => (
          <div key={r.id} className="flex items-start gap-2 border-b border-wf-line pb-2" data-testid={`task-part-row-${i}`}>
            <div className="flex flex-1 flex-col gap-1.5">
              <select data-testid={`task-part-pick-${i}`} className={sel} value={r.custom ? OTHER : r.part_id}
                onChange={(e) => {
                  if (e.target.value === OTHER) return patch(r.id, { custom: true, part_id: "", part_number: "", name: "" });
                  const p = catalog.data?.find((x) => x.id === e.target.value);
                  if (p) patch(r.id, { custom: false, part_id: p.id, name: p.name, part_number: p.part_number });
                }}>
                <option value="">Choose a part</option>
                {(catalog.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                <option value={OTHER}>Other (type a name)</option>
              </select>
              {r.custom && <input data-testid={`task-part-name-${i}`} className={sel} value={r.name} onChange={(e) => patch(r.id, { name: e.target.value })} placeholder="Part name, e.g. 18/4 wire" />}
            </div>
            <input data-testid={`task-part-qty-${i}`} type="number" step="any" className={`${sel} !w-20 text-center`} value={r.qty} onChange={(e) => patch(r.id, { qty: e.target.value })} placeholder="Qty" />
            <button data-testid={`task-part-remove-${i}`} onClick={() => setRows((rs) => rs.filter((x) => x.id !== r.id))} className="mt-1.5 rounded-full p-2 text-wf-mute hover:text-wf-err"><Trash2 size={17} /></button>
          </div>
        ))}
        {!rows.length && <p className="text-sm text-wf-mute">No parts yet.</p>}
      </div>
      <div className="flex justify-between gap-2">
        <Btn variant="secondary" icon={Plus} data-testid="task-part-add" onClick={() => setRows((rs) => [...rs, { id: newId(), part_id: "", name: "", part_number: "", qty: "1", custom: false }])}>Add Part</Btn>
        <Btn loading={saving} onClick={save} data-testid="task-parts-save">Save Parts</Btn>
      </div>
    </Modal>
  );
}
